import React, { useCallback, useEffect, useState } from 'react'
import { Amplify, Auth as amplifyAuth } from 'aws-amplify'
import axios from 'axios'
import config from 'config'
import i18n, { defaultLanguage } from 'i18n'

import logger from '../logger'

import axiosHook from './axiosHook'

export const AuthContext = React.createContext('auth')

export const ROLE_SUPER_ADMIN = 'superAdmin'
export const ROLE_ADMIN = 'admin'
export const ROLE_PARTNER_ADMIN = 'partnerAdmin'
export const ROLE_READ_ONLY = 'readOnly'
export const ROLE_PARTNER_READ_ONLY = 'partnerReadOnly'
export const ROLE_B2C = 'b2c'

export const ROLES_SUPERADMIN = [ROLE_SUPER_ADMIN]

export const ROLES_SIXT = [
  ROLE_SUPER_ADMIN,
  ROLE_ADMIN,
  ROLE_READ_ONLY,
  ROLE_B2C,
]
export const ROLES_PARTNER = [ROLE_PARTNER_ADMIN, ROLE_PARTNER_READ_ONLY]

Amplify.configure(config.auth)

export const AuthContextProvider = ({ children }) => {
  const [authEvent, setAuthEvent] = useState()
  const [currentSession, setSession] = useState()
  const [claims, setClaims] = useState()
  const [language, setLanguage] = useState(defaultLanguage)

  const signOut = () => {
    amplifyAuth.signOut()
    setTimeout(() => {
      setAuthEvent()
      setSession()
    }, 100)
  }

  useEffect(() => {
    axios.interceptors.request.use(axiosHook(signOut))
  }, [])

  const handleLanguage = (claims) => {
    const currentLanguage = localStorage.getItem('language')
    const newLanguage = claims?.language ?? currentLanguage ?? defaultLanguage
    i18n.changeLanguage(newLanguage)

    if (currentLanguage !== newLanguage) {
      localStorage.setItem('language', newLanguage)
    }
    setLanguage(newLanguage)
  }

  const updateAuth = useCallback(async () => {
    try {
      const auth = await amplifyAuth.currentAuthenticatedUser()

      const session = await amplifyAuth.currentSession()

      const claims = JSON.parse(
        session?.idToken?.payload[config.auth.claims] || '{}'
      )

      setClaims(claims)
      setAuthEvent(auth)
      setSession(session)

      handleLanguage(claims)

      return {
        auth,
        session,
        claims,
      }
    } catch (e) {
      logger.error(e)
      setAuthEvent(null)
      handleLanguage()
    }
  }, [])

  const refreshToken = async () => {
    try {
      const auth = await amplifyAuth.currentAuthenticatedUser()
      const session = await amplifyAuth.currentSession()
      await auth.refreshSession(session.refreshToken, async (err, session) => {
        await updateAuth()
      })
    } catch (e) {
      logger('Unable to refresh Token', e)
    }
  }

  const getAccessToken = useCallback(async () => {
    return (await amplifyAuth.currentSession())?.idToken.jwtToken
  }, [])

  useEffect(() => {
    const run = async () => {
      updateAuth()
    }
    run()
  }, [setAuthEvent, updateAuth])

  return (
    <AuthContext.Provider
      value={{
        setAuthEvent,
        authEvent,
        claims,
        currentSession,
        language,
        updateAuth,
        refreshToken,
        AmplifyAuth: amplifyAuth,
        signOut,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
